"use client";
import React, { createContext, useState, useContext, useEffect } from "react";
import { toast } from "react-toastify";
import { BASE_URL } from "../config/apiConfig";

export const DepartmentContext = createContext();

export const DepartmentProvider = ({ children }) => {
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // ✅ Get token from localStorage
  const getToken = () => localStorage.getItem("token");

  // ✅ Safe JSON parser
  const parseJson = async (response) => {
    try {
      return await response.json();
    } catch {
      return null;
    }
  };

  // ✅ Fetch all departments
  const fetchDepartments = async () => {
    setLoading(true);
    setError("");

    try {
      const token = getToken();

      const res = await fetch(`${BASE_URL}/departments`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!res.ok) {
        setError("Failed to fetch departments");
        return;
      }

      const data = await parseJson(res);

      if (!data) {
        setError("Invalid server response");
        return;
      }

      if (data.status === 200) {
        setDepartments(data.data || []);
      } else {
        setError(data.message || "Failed to fetch departments");
      }
    } catch (err) {
      console.error(err);
      setError("Error fetching departments");
    } finally {
      setLoading(false);
    }
  };

  // ✅ Create department
  const createDepartment = async (payload) => {
    setLoading(true);
    setError("");

    try {
      const token = getToken();

      const res = await fetch(`${BASE_URL}/departments`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const data = await parseJson(res);
        return {
          success: false,
          message: data?.message || "Failed to create department",
        };
      }

      const data = await parseJson(res);

      if (!data) {
        return {
          success: false,
          message: "Invalid server response",
        };
      }

      if (data.status === 201) {
        await fetchDepartments();

        toast.success(data.message || "Department created successfully");

        return {
          success: true,
          message: data.message || "Department created successfully",
          data,
        };
      }

      return {
        success: false,
        message: data.message || "Failed to create department",
      };
    } catch (err) {
      console.error(err);

      return {
        success: false,
        message: "Error creating department",
      };
    } finally {
      setLoading(false);
    }
  };

  // ✅ Update department
  const updateDepartment = async (id, payload) => {
    setLoading(true);

    try {
      const token = getToken();

      const res = await fetch(`${BASE_URL}/departments/${id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const data = await parseJson(res);
        return {
          success: false,
          message: data?.message || "Failed to update department",
        };
      }

      const data = await parseJson(res);

      if (!data) {
        return {
          success: false,
          message: "Invalid server response",
        };
      }

      if (data.status === 200) {
        await fetchDepartments();

        toast.success(data.message || "Department updated successfully");

        return {
          success: true,
          message: data.message || "Department updated successfully",
          data,
        };
      }

      return {
        success: false,
        message: data.message || "Failed to update department",
      };
    } catch (err) {
      console.error(err);

      return {
        success: false,
        message: "Error updating department",
      };
    } finally {
      setLoading(false);
    }
  };

  // ✅ Update status
  const updateDepartmentStatus = async (id, status, update_audit_id) => {
    try {
      const token = getToken();

      const res = await fetch(`${BASE_URL}/departments/status/${id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ status, update_audit_id }),
      });

      if (!res.ok) {
        const data = await parseJson(res);
        return {
          success: false,
          message: data?.message || "Failed to update department status",
        };
      }

      const data = await parseJson(res);

      if (!data) {
        return {
          success: false,
          message: "Invalid server response",
        };
      }

      if (data.status === 200) {
        await fetchDepartments();

        toast.success(
          data.message || "Department status updated successfully"
        );

        return {
          success: true,
          message: data.message,
          data,
        };
      }

      return {
        success: false,
        message: data.message || "Failed to update department status",
      };
    } catch (err) {
      console.error(err);

      return {
        success: false,
        message: "Error updating department status",
      };
    }
  };

  // ✅ Delete department (soft delete)
  const deleteDepartment = async (id, delete_audit_id) => {
    try {
      const token = getToken();

      const res = await fetch(`${BASE_URL}/departments/${id}`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ delete_audit_id }),
      });

      if (!res.ok) {
        const data = await parseJson(res);
        return {
          success: false,
          message: data?.message || "Failed to delete department",
        };
      }

      const data = await parseJson(res);

      if (!data) {
        return {
          success: false,
          message: "Invalid server response",
        };
      }

      if (data.status === 200) {
        await fetchDepartments();

        toast.success(data.message || "Department deleted successfully");

        return {
          success: true,
          message: data.message || "Department deleted successfully",
          data,
        };
      }

      return {
        success: false,
        message: data.message || "Failed to delete department",
      };
    } catch (err) {
      console.error(err);

      return {
        success: false,
        message: "Error deleting department",
      };
    }
  };

  // ✅ Fetch on load
  useEffect(() => {
    const token = getToken();
    if (token) {
      fetchDepartments();
    }
  }, []);

  return (
    <DepartmentContext.Provider
      value={{
        departments,
        loading,
        error,
        fetchDepartments,
        createDepartment,
        updateDepartment,
        updateDepartmentStatus,
        deleteDepartment,
      }}
    >
      {children}
    </DepartmentContext.Provider>
  );
};

export const useDepartments = () => useContext(DepartmentContext);